import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Combobox } from '@/components/Combobox'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/cn'
import { ApiError } from '@/lib/api'
import { useReportBrand } from '@/lib/pdf'
import { coaApi } from '@/features/coa/api'
import {
  useCoa,
  useCreateSubcategory,
  useDeleteMainCategory,
  useDeleteSubcategory,
  useRenameMainCategory,
  useUpdateSubcategory,
} from '@/features/coa/queries'
import type { TaxonomyItem } from '@/features/coa/types'
import {
  useControlAccounts,
  useUpdateControlAccounts,
} from '@/features/controlAccounts/queries'
import type { ControlAccounts } from '@/features/controlAccounts/api'

type Tab = 'Balance Sheet' | 'Income Statement' | 'Control Accounts'

const TABS: Tab[] = ['Balance Sheet', 'Income Statement', 'Control Accounts']
type StatementTab = 'Balance Sheet' | 'Income Statement'

function errorMessage(err: unknown, fallback = 'An unknown error occurred.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function CoaPage() {
  const { toast } = useToast()
  const q = useCoa()
  const brand = useReportBrand()

  const [activeTab, setActiveTab] = useState<Tab>('Balance Sheet')
  // The tree UI only makes sense for BS/IS. When the user flips to
  // Control Accounts, freeze the statement type at its last value so
  // switching back lands them where they were.
  const lastStatementTab: StatementTab =
    activeTab === 'Control Accounts' ? 'Balance Sheet' : activeTab
  const [openCategory, setOpenCategory] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const [categoryModal, setCategoryModal] = useState<
    | { kind: 'add' }
    | { kind: 'edit'; originalName: string }
    | null
  >(null)
  const [subcategoryModal, setSubcategoryModal] = useState<
    | { kind: 'add'; mainCategory: string }
    | { kind: 'edit'; mainCategory: string; item: TaxonomyItem }
    | null
  >(null)
  const [confirmDeleteCategory, setConfirmDeleteCategory] = useState<string | null>(null)
  const [confirmDeleteSubcategory, setConfirmDeleteSubcategory] = useState<TaxonomyItem | null>(null)
  const [infoModal, setInfoModal] = useState<{ title: string; body: React.ReactNode } | null>(null)

  const taxonomy = useMemo(() => q.data ?? [], [q.data])

  const statementData = useMemo(() => {
    const out: Record<string, TaxonomyItem[]> = {}
    for (const item of taxonomy) {
      if (item.statement_type !== lastStatementTab) continue
      if (!out[item.main_category]) out[item.main_category] = []
      out[item.main_category].push(item)
    }
    return out
  }, [taxonomy, lastStatementTab])

  const needle = searchTerm.trim().toLowerCase()

  const filteredData = useMemo(() => {
    if (!needle) return statementData
    const out: Record<string, TaxonomyItem[]> = {}
    for (const [cat, items] of Object.entries(statementData)) {
      const catMatches = cat.toLowerCase().includes(needle)
      const matchedItems = items.filter(
        (i) =>
          i.subcategory_name.toLowerCase().includes(needle) ||
          i.account_code.toLowerCase().includes(needle),
      )
      if (catMatches || matchedItems.length > 0) {
        // When the category itself matches, show all its subs; otherwise
        // only the matching subs so the user lands right on their result.
        out[cat] = catMatches ? items : matchedItems
      }
    }
    return out
  }, [statementData, needle])

  const categoryNames = useMemo(() => Object.keys(filteredData), [filteredData])

  const totalMatches = useMemo(
    () => Object.values(filteredData).reduce((n, items) => n + items.length, 0),
    [filteredData],
  )

  // When a search is active and there's only one match, auto-open it.
  useEffect(() => {
    if (needle && categoryNames.length === 1 && openCategory !== categoryNames[0]) {
      setOpenCategory(categoryNames[0])
    }
  }, [needle, categoryNames, openCategory])

  const createSub = useCreateSubcategory()
  const updateSub = useUpdateSubcategory()
  const deleteSub = useDeleteSubcategory()
  const renameMain = useRenameMainCategory()
  const deleteMain = useDeleteMainCategory()

  const busy =
    createSub.isPending ||
    updateSub.isPending ||
    deleteSub.isPending ||
    renameMain.isPending ||
    deleteMain.isPending

  const handleExportCsv = async () => {
    if (taxonomy.length === 0) {
      setInfoModal({ title: 'Nothing to export', body: 'No Chart of Accounts data to download.' })
      return
    }
    try {
      const [user, mod] = await Promise.all([coaApi.profile(), import('@/features/coa/export')])
      mod.downloadCoaCsv(taxonomy, user)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not export CSV',
        description: errorMessage(err),
      })
    }
  }

  const handleExportPdf = async () => {
    if (taxonomy.length === 0) {
      setInfoModal({ title: 'Nothing to export', body: 'No Chart of Accounts data to download.' })
      return
    }
    try {
      const [user, mod] = await Promise.all([coaApi.profile(), import('@/features/coa/export')])
      mod.downloadCoaPdf(taxonomy, user, brand)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not export PDF',
        description: errorMessage(err),
      })
    }
  }

  const requestDeleteCategory = (name: string) => {
    if (categoryNames.length <= 1) {
      setInfoModal({
        title: 'Cannot delete',
        body: (
          <>
            <p>
              You cannot delete the last category. At least one main category is required for the
              {' '}
              <strong>{lastStatementTab}</strong>.
            </p>
            <p className="mt-2">
              If you need to restructure, add a new category first, then delete this one.
            </p>
          </>
        ),
      })
      return
    }
    setConfirmDeleteCategory(name)
  }

  const runDeleteCategory = async () => {
    if (!confirmDeleteCategory) return
    try {
      await deleteMain.mutateAsync({ statementType: lastStatementTab, name: confirmDeleteCategory })
      toast({ title: `Deleted "${confirmDeleteCategory}".` })
      if (openCategory === confirmDeleteCategory) setOpenCategory(null)
      setConfirmDeleteCategory(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: errorMessage(err),
      })
    }
  }

  const runDeleteSubcategory = async () => {
    if (!confirmDeleteSubcategory) return
    try {
      await deleteSub.mutateAsync(confirmDeleteSubcategory.id)
      toast({ title: `Deleted "${confirmDeleteSubcategory.subcategory_name}".` })
      setConfirmDeleteSubcategory(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: errorMessage(err),
      })
    }
  }

  if (q.isLoading) {
    return (
      <Page title="Chart of Accounts">
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      </Page>
    )
  }

  if (q.isError) {
    return (
      <Page title="Chart of Accounts">
        <Alert variant="destructive">
          <AlertDescription>
            Could not load the Chart of Accounts: {errorMessage(q.error)}
          </AlertDescription>
        </Alert>
      </Page>
    )
  }

  const allCategoryCount = Object.keys(statementData).length
  const totalCategories = categoryNames.length
  const hasAnyCategories = allCategoryCount > 0

  return (
    <Page
      title="Chart of Accounts"
      description="Manage the account taxonomy used to classify every transaction."
      width="wide"
      actions={
        <>
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download className="size-4" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPdf}>
            <FileText className="size-4" /> PDF
          </Button>
        </>
      }
    >
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-2">
              {TABS.map((tab) => (
                <Button
                  key={tab}
                  variant={activeTab === tab ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setActiveTab(tab)
                    setOpenCategory(null)
                    setSearchTerm('')
                  }}
                >
                  {tab}
                </Button>
              ))}
            </div>
            {activeTab !== 'Control Accounts' ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCategoryModal({ kind: 'add' })}
                  disabled={busy}
                >
                  <Plus className="size-4" /> Add category
                </Button>
                <div className="ml-auto text-sm text-muted-foreground">
                  Total categories: <span className="font-semibold text-foreground">{allCategoryCount}</span>
                </div>
              </>
            ) : null}
          </div>

          {activeTab === 'Control Accounts' ? (
            <ControlAccountsTab taxonomy={taxonomy} />
          ) : (
          <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              type="search"
              placeholder="Search by category, account name, or code (e.g. 1001)…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              className="pl-9"
              aria-label="Search chart of accounts"
            />
            {needle ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {totalMatches === 0
                  ? 'No matching accounts.'
                  : `${totalMatches} account${totalMatches === 1 ? '' : 's'} in ${categoryNames.length} categor${categoryNames.length === 1 ? 'y' : 'ies'}.`}
              </p>
            ) : null}
          </div>

          {!hasAnyCategories ? (
            <EmptyState
              title={`No ${lastStatementTab.toLowerCase()} categories yet`}
              description="Add a main category to start building this statement type."
              action={
                <Button onClick={() => setCategoryModal({ kind: 'add' })}>
                  <Plus className="size-4" /> Add category
                </Button>
              }
            />
          ) : totalCategories === 0 ? (
            <EmptyState
              title="No matches"
              description={`Nothing in ${lastStatementTab} matches "${searchTerm.trim()}".`}
            />
          ) : (
            <div className="flex flex-col gap-2">
              {categoryNames.map((name) => {
                const isOpen = openCategory === name
                const subs = filteredData[name] ?? []
                return (
                  <div
                    key={name}
                    className={cn(
                      'rounded-md border bg-card transition-colors',
                      isOpen ? 'border-primary/40 shadow-card' : 'hover:bg-accent/40',
                    )}
                  >
                    <Button
                      variant="ghost"
                      onClick={() => setOpenCategory(isOpen ? null : name)}
                      className="flex h-auto w-full items-center justify-between gap-3 rounded-none px-4 py-3 text-left font-normal hover:bg-transparent"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        {isOpen ? (
                          <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                        ) : (
                          <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                        )}
                        <h3 className="truncate text-base font-semibold text-foreground">{name}</h3>
                        <span className="text-xs text-muted-foreground">
                          ({subs.length} {subs.length === 1 ? 'account' : 'accounts'})
                        </span>
                      </div>
                      {!isOpen ? (
                        <span
                          className="flex shrink-0 items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                          role="presentation"
                        >
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation()
                              setCategoryModal({ kind: 'edit', originalName: name })
                            }}
                            title="Rename category"
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation()
                              requestDeleteCategory(name)
                            }}
                            title="Delete category"
                          >
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </span>
                      ) : null}
                    </Button>

                    {isOpen ? (
                      <div className="border-t px-4 py-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">
                            Subcategories: {subs.length}
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setSubcategoryModal({ kind: 'add', mainCategory: name })}
                            >
                              <Plus className="size-4" /> Add subcategory
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setCategoryModal({ kind: 'edit', originalName: name })}
                              title="Rename category"
                            >
                              <Pencil className="size-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => requestDeleteCategory(name)}
                              title="Delete category"
                            >
                              <Trash2 className="size-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                        {subs.length === 0 ? (
                          <p className="py-2 text-xs text-muted-foreground">
                            No subcategories yet.
                          </p>
                        ) : (
                          <div className="overflow-hidden rounded-md border">
                            <div className="grid grid-cols-[1fr_140px_100px] items-center border-b bg-muted/50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              <div>Subcategory</div>
                              <div>Account code</div>
                              <div className="text-right">Actions</div>
                            </div>
                            {subs.map((item) => (
                              <div
                                key={item.id}
                                className="grid grid-cols-[1fr_140px_100px] items-center border-b px-3 py-2 text-sm last:border-b-0"
                              >
                                <div className="truncate">{item.subcategory_name}</div>
                                <div className="font-mono text-xs">{item.account_code}</div>
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      setSubcategoryModal({ kind: 'edit', mainCategory: name, item })
                                    }
                                    title="Edit subcategory"
                                  >
                                    <Pencil className="size-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setConfirmDeleteSubcategory(item)}
                                    title="Delete subcategory"
                                  >
                                    <Trash2 className="size-4 text-destructive" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}
          </>
          )}
        </CardContent>
      </Card>

      <CategoryModal
        state={categoryModal}
        activeTab={lastStatementTab}
        existingNames={categoryNames}
        onClose={() => setCategoryModal(null)}
        onRename={async ({ oldName, newName }) => {
          try {
            await renameMain.mutateAsync({ statementType: lastStatementTab, oldName, newName })
            toast({ title: `Renamed to "${newName}".` })
            if (openCategory === oldName) setOpenCategory(newName)
            setCategoryModal(null)
          } catch (err) {
            toast({
              variant: 'destructive',
              title: 'Rename failed',
              description: errorMessage(err),
            })
          }
        }}
        onAddRequiresSubcategory={(name) => {
          setCategoryModal(null)
          setSubcategoryModal({ kind: 'add', mainCategory: name })
        }}
      />

      <SubcategoryModal
        state={subcategoryModal}
        activeTab={lastStatementTab}
        taxonomy={taxonomy}
        busy={createSub.isPending || updateSub.isPending}
        onClose={() => setSubcategoryModal(null)}
        onSave={async ({ id, payload }) => {
          try {
            if (id != null) {
              await updateSub.mutateAsync({ id, body: payload })
              toast({ title: 'Subcategory updated.' })
            } else {
              await createSub.mutateAsync(payload)
              toast({ title: 'Subcategory created.' })
            }
            setOpenCategory(payload.main_category)
            setSubcategoryModal(null)
          } catch (err) {
            toast({
              variant: 'destructive',
              title: 'Save failed',
              description: errorMessage(err),
            })
          }
        }}
        onInvalid={(info) => setInfoModal(info)}
      />

      <Modal.Confirm
        open={confirmDeleteCategory !== null}
        onOpenChange={(open) => !open && setConfirmDeleteCategory(null)}
        title="Delete category?"
        body={
          confirmDeleteCategory ? (
            <>
              <p>
                Delete <strong>{confirmDeleteCategory}</strong> and all{' '}
                {(statementData[confirmDeleteCategory] ?? []).length} subcategor
                {(statementData[confirmDeleteCategory] ?? []).length === 1 ? 'y' : 'ies'}?
              </p>
              <p className="mt-2 text-destructive">
                Warning: if any accounts in this category have posted transactions, those entries will
                become orphaned. Make sure no active accounts exist here before deleting.
              </p>
            </>
          ) : null
        }
        onConfirm={runDeleteCategory}
        onCancel={() => setConfirmDeleteCategory(null)}
        busy={deleteMain.isPending}
      />

      <Modal.Confirm
        open={confirmDeleteSubcategory !== null}
        onOpenChange={(open) => !open && setConfirmDeleteSubcategory(null)}
        title="Delete subcategory?"
        body={
          confirmDeleteSubcategory ? (
            <>
              <p>
                Delete subcategory{' '}
                <strong>{confirmDeleteSubcategory.subcategory_name}</strong> (
                {confirmDeleteSubcategory.account_code})?
              </p>
              <p className="mt-2 text-destructive">
                If this account has posted transactions or is used in TB Import mappings, those
                references will break. Ensure no entries use this account before deleting.
              </p>
            </>
          ) : null
        }
        onConfirm={runDeleteSubcategory}
        onCancel={() => setConfirmDeleteSubcategory(null)}
        busy={deleteSub.isPending}
      />

      <Modal.Alert
        open={infoModal !== null}
        onOpenChange={(open) => !open && setInfoModal(null)}
        title={infoModal?.title ?? ''}
        body={infoModal?.body}
        onClose={() => setInfoModal(null)}
      />
    </Page>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Category modal (add + rename)
// ──────────────────────────────────────────────────────────────────────────

const categorySchema = z.object({
  name: z.string().trim().min(1, 'Category name is required.'),
})
type CategoryForm = z.infer<typeof categorySchema>

function CategoryModal({
  state,
  activeTab,
  existingNames,
  onClose,
  onRename,
  onAddRequiresSubcategory,
}: {
  state: { kind: 'add' } | { kind: 'edit'; originalName: string } | null
  activeTab: StatementTab
  existingNames: string[]
  onClose: () => void
  onRename: (args: { oldName: string; newName: string }) => void | Promise<void>
  onAddRequiresSubcategory: (name: string) => void
}) {
  const open = state !== null
  const isEdit = state?.kind === 'edit'
  const originalName = state?.kind === 'edit' ? state.originalName : ''

  const form = useForm<CategoryForm>({
    resolver: zodResolver(categorySchema),
    values: { name: originalName },
  })

  const onSubmit = form.handleSubmit((values) => {
    const newName = values.name.trim()
    const duplicate = existingNames.some(
      (n) =>
        n.toLowerCase() === newName.toLowerCase() &&
        (!isEdit || n.toLowerCase() !== originalName.toLowerCase()),
    )
    if (duplicate) {
      form.setError('name', {
        type: 'duplicate',
        message: `A main category named "${newName}" already exists in the ${activeTab}.`,
      })
      return
    }
    if (isEdit) {
      if (newName === originalName) {
        onClose()
        return
      }
      void onRename({ oldName: originalName, newName })
    } else {
      onAddRequiresSubcategory(newName)
    }
  })

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={isEdit ? 'Rename main category' : 'Add main category'}
      description={
        isEdit
          ? undefined
          : 'Categories are created together with their first subcategory. Save to continue to the subcategory form.'
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={form.formState.isSubmitting}>
            {isEdit ? 'Save' : 'Continue'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void onSubmit()
        }}
        className="space-y-3"
      >
        <FormField
          label="Category name"
          required
          autoFocus
          {...form.register('name')}
          error={form.formState.errors.name?.message}
        />
        <FormField label="Statement type" value={activeTab} disabled readOnly />
      </form>
    </Modal>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Subcategory modal (add + edit)
// ──────────────────────────────────────────────────────────────────────────

const ACCOUNT_CODE_RANGES = (
  <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
    <li>1xxx - Assets</li>
    <li>2xxx - Liabilities</li>
    <li>3xxx - Equity</li>
    <li>4xxx - Revenue</li>
    <li>5xxx - Cost of Goods Sold</li>
    <li>6xxx–7xxx - Expenses</li>
  </ul>
)

const subcategorySchema = z.object({
  subcategory_name: z.string().trim().min(1, 'Subcategory name is required.'),
  account_code: z
    .string()
    .trim()
    .regex(/^\d{3,6}$/, 'Account codes must be 3–6 digits (e.g. 1001, 6050).'),
})
type SubcategoryForm = z.infer<typeof subcategorySchema>

function SubcategoryModal({
  state,
  activeTab,
  taxonomy,
  busy,
  onClose,
  onSave,
  onInvalid,
}: {
  state:
    | { kind: 'add'; mainCategory: string }
    | { kind: 'edit'; mainCategory: string; item: TaxonomyItem }
    | null
  activeTab: StatementTab
  taxonomy: TaxonomyItem[]
  busy: boolean
  onClose: () => void
  onSave: (args: {
    id: number | null
    payload: {
      statement_type: StatementType
      main_category: string
      subcategory_name: string
      account_code: string
    }
  }) => void | Promise<void>
  onInvalid: (info: { title: string; body: React.ReactNode }) => void
}) {
  const open = state !== null
  const mainCategory = state?.mainCategory ?? ''
  const isEdit = state?.kind === 'edit'
  const existingId = state?.kind === 'edit' ? state.item.id : null

  const initial: SubcategoryForm =
    state?.kind === 'edit'
      ? { subcategory_name: state.item.subcategory_name, account_code: state.item.account_code }
      : { subcategory_name: '', account_code: '' }

  const form = useForm<SubcategoryForm>({
    resolver: zodResolver(subcategorySchema),
    values: initial,
  })

  const onSubmit = form.handleSubmit((values) => {
    const subcategory_name = values.subcategory_name.trim()
    const account_code = values.account_code.trim()

    const dupName = taxonomy.some(
      (item) =>
        item.statement_type === activeTab &&
        item.main_category === mainCategory &&
        item.subcategory_name.toLowerCase() === subcategory_name.toLowerCase() &&
        (!isEdit || item.id !== existingId),
    )
    if (dupName) {
      onInvalid({
        title: 'Duplicate name',
        body: (
          <p>
            A subcategory named <strong>{subcategory_name}</strong> already exists in {mainCategory}.
          </p>
        ),
      })
      return
    }

    const dupCode = taxonomy.some(
      (item) =>
        item.statement_type === activeTab &&
        item.main_category === mainCategory &&
        item.account_code === account_code &&
        (!isEdit || item.id !== existingId),
    )
    if (dupCode) {
      onInvalid({
        title: 'Duplicate account code',
        body: (
          <p>
            Account code <strong>{account_code}</strong> already exists in {mainCategory}.
          </p>
        ),
      })
      return
    }

    void onSave({
      id: existingId,
      payload: {
        statement_type: activeTab,
        main_category: mainCategory,
        subcategory_name,
        account_code,
      },
    })
  })

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={
        isEdit
          ? `Edit subcategory in ${mainCategory}`
          : `Add subcategory to ${mainCategory}`
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={busy}>
            Save
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void onSubmit()
        }}
        className="space-y-3"
      >
        <FormField
          label="Subcategory name"
          required
          autoFocus
          placeholder="e.g. Cash in Bank"
          {...form.register('subcategory_name')}
          error={form.formState.errors.subcategory_name?.message}
        />
        <FormField
          label="Account code"
          required
          placeholder="e.g. 1001"
          {...form.register('account_code')}
          error={form.formState.errors.account_code?.message}
          hint={
            form.formState.errors.account_code
              ? null
              : 'Use a 3–6 digit numeric code.'
          }
        />
        {!isEdit && !form.formState.errors.account_code ? (
          <div className="rounded-md bg-muted/50 px-3 py-2 text-xs">
            <p className="font-medium text-foreground">Standard ranges</p>
            {ACCOUNT_CODE_RANGES}
          </div>
        ) : null}
      </form>
    </Modal>
  )
}

type StatementType = 'Balance Sheet' | 'Income Statement' | (string & {})

// ──────────────────────────────────────────────────────────────────────────
// Control Accounts tab - picks which CoA row plays each of the 8 control
// roles (AR, AP, Cash, Tax Receivable, Sales Tax Payable, Retained
// Earnings, Sales Returns, Bad Debt). Options are filtered to the right
// statement_type so the user can't map AR to an Income Statement account.
// ──────────────────────────────────────────────────────────────────────────

type ControlField = {
  key: keyof ControlAccounts
  label: string
  description: string
  expectedStatement: 'Balance Sheet' | 'Income Statement'
}

const CONTROL_FIELDS: ControlField[] = [
  { key: 'ar_code',                label: 'Accounts Receivable (AR)',       description: 'Debited when you post an invoice; credited when the customer pays.',       expectedStatement: 'Balance Sheet' },
  { key: 'ap_code',                label: 'Accounts Payable (AP)',           description: 'Credited when you post a bill; debited when you pay the vendor.',          expectedStatement: 'Balance Sheet' },
  { key: 'cash_code',              label: 'Cash / Operating bank',           description: 'Debited on customer payment; credited on vendor payment or refund.',       expectedStatement: 'Balance Sheet' },
  { key: 'tax_receivable_code',    label: 'Tax Receivable (VAT / GST)',      description: 'Debited on bill tax lines - the tax you can reclaim from authorities.',   expectedStatement: 'Balance Sheet' },
  { key: 'sales_tax_payable_code', label: 'Sales Tax Payable',               description: 'Credited on invoice tax lines - the tax you owe the authorities.',       expectedStatement: 'Balance Sheet' },
  { key: 'retained_earnings_code', label: 'Retained Earnings',               description: 'Used by the year-end close to move Net Income into Equity.',             expectedStatement: 'Balance Sheet' },
  { key: 'sales_returns_code',     label: 'Sales Returns / Contra-revenue',  description: 'Debited when you issue a credit note (reduces revenue).',                expectedStatement: 'Income Statement' },
  { key: 'bad_debt_code',          label: 'Bad Debt Expense',                description: 'Debited when you write off an uncollectible invoice.',                    expectedStatement: 'Income Statement' },
]

function ControlAccountsTab({ taxonomy }: { taxonomy: TaxonomyItem[] }) {
  const { toast } = useToast()
  const { data: remote, isLoading } = useControlAccounts()
  const updateMut = useUpdateControlAccounts()
  const [draft, setDraft] = useState<Partial<ControlAccounts>>({})

  useEffect(() => {
    if (remote?.control_accounts) setDraft({ ...remote.control_accounts })
  }, [remote?.control_accounts])

  const optionsByStatement = useMemo(() => {
    const map: Record<'Balance Sheet' | 'Income Statement', { value: string; label: string; description?: string }[]> = {
      'Balance Sheet': [], 'Income Statement': [],
    }
    for (const row of taxonomy) {
      if (!row.account_code || !row.statement_type) continue
      const st = row.statement_type as string
      if (st !== 'Balance Sheet' && st !== 'Income Statement') continue
      map[st as 'Balance Sheet' | 'Income Statement'].push({
        value: row.account_code,
        label: `${row.account_code} · ${row.subcategory_name}`,
        description: row.main_category,
      })
    }
    ;(Object.keys(map) as Array<keyof typeof map>).forEach(k => {
      map[k].sort((a, b) => a.value.localeCompare(b.value))
    })
    return map
  }, [taxonomy])

  const hasChanges = useMemo(() => {
    if (!remote?.control_accounts) return false
    return CONTROL_FIELDS.some(f => draft[f.key] !== remote.control_accounts[f.key])
  }, [draft, remote])

  async function onSave() {
    if (!hasChanges || !remote?.control_accounts) return
    const patch: Partial<ControlAccounts> = {}
    for (const f of CONTROL_FIELDS) {
      if (draft[f.key] && draft[f.key] !== remote.control_accounts[f.key]) {
        patch[f.key] = draft[f.key]
      }
    }
    try {
      await updateMut.mutateAsync(patch)
      toast({ title: 'Control accounts updated' })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  if (isLoading) {
    return <div className="flex justify-center py-10"><LoadingSpinner /></div>
  }

  return (
    <div className="space-y-4">
      <Alert>
        <AlertDescription>
          Pick which accounts from your chart of accounts the posting engine uses for AR, AP, Cash and the other control accounts. Every invoice, bill, payment, credit note, write-off, and year-end close posts to the codes you choose here.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {CONTROL_FIELDS.map(f => {
          const options = optionsByStatement[f.expectedStatement]
          return (
            <div key={f.key} className="grid gap-3 rounded-md border bg-card p-3 md:grid-cols-[1fr_1fr]">
              <div>
                <div className="text-sm font-semibold">{f.label}</div>
                <div className="text-xs text-muted-foreground">{f.description}</div>
                <div className="mt-1 text-[0.65rem] uppercase tracking-wide text-muted-foreground">
                  Statement: {f.expectedStatement}
                </div>
              </div>
              <FormField
                label=""
                control={
                  <Combobox
                    value={draft[f.key] || null}
                    onChange={v => setDraft(d => ({ ...d, [f.key]: v ? String(v) : '' }))}
                    options={options}
                    placeholder={`Pick a ${f.expectedStatement} account…`}
                    emptyMessage={`No ${f.expectedStatement} accounts in your CoA.`}
                  />
                }
              />
            </div>
          )
        })}
      </div>

      <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
        <p><strong className="text-foreground">How this works.</strong> Every posting flow reads these codes at the moment it runs. Change one here and the next posting uses the new code. Already-posted entries keep the code they were written with - historical reports are never retroactively rewritten.</p>
        <p className="mt-2"><strong className="text-foreground">Safety guards.</strong> Once a code is mapped here, this page will block you from renaming or deleting that code on the Balance Sheet or Income Statement tabs until you remap it to a different one. Same for any code that already has posted entries - you can still rename the description, but the code itself is locked so historical data stays intact.</p>
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={!hasChanges || updateMut.isPending}>
          <Save className="mr-1 size-4" /> Save
        </Button>
      </div>
    </div>
  )
}
